import { createContext, useContext, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { getAmbientEngine } from "@/audio/ambient";

const SoundContext = createContext(null);

export const useSound = () => {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error("useSound must be used within SoundProvider");
  return ctx;
};

export const SoundProvider = ({ children }) => {
  // Always silent by default on load. Preference is remembered in-memory across
  // SPA navigation (and mirrored to sessionStorage for the current session).
  const [enabled, setEnabled] = useState(false);
  const engineRef = useRef(getAmbientEngine());

  const enableSound = useCallback(() => {
    engineRef.current.fadeIn(3);
    setEnabled(true);
    try {
      sessionStorage.setItem("svara-sound", "on");
    } catch (e) {
      /* ignore */
    }
    toast("Sound experience enabled", {
      description: "Take a slow breath and explore at your own pace.",
      duration: 5000,
      icon: "\u266A",
    });
  }, []);

  const disableSound = useCallback(() => {
    engineRef.current.fadeOut(2);
    setEnabled(false);
    try {
      sessionStorage.setItem("svara-sound", "off");
    } catch (e) {
      /* ignore */
    }
  }, []);

  const toggleSound = useCallback(() => {
    if (enabled) disableSound();
    else enableSound();
  }, [enabled, enableSound, disableSound]);

  // One-shot shimmering bell (used by the numerology reveal). Silent unless
  // the ambient sound experience is currently enabled.
  const playChime = useCallback(() => {
    if (enabled) engineRef.current.playChime();
  }, [enabled]);

  return (
    <SoundContext.Provider value={{ enabled, enableSound, disableSound, toggleSound, playChime }}>
      {children}
    </SoundContext.Provider>
  );
};
